import { isEmpty, isNil, sum } from 'lodash';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import {
  RelatedEntity,
  RelatedEntityField,
  RelatedItem,
  useRelatedEntities,
} from '@shared/data/entity/fields/RelatedEntityField';
import { useGetChannel } from '@shared/scenes/channel/hooks';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { FieldRecord } from '@shared/scenes/entity/types';
import { openCard, useGetWorkflowTemplateLazyQuery } from '@shared/scenes/workflows/hooks';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { getTestId } from '@shared/util/getTestId';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useNavigator } from '@shared/util/navigation';
import { notNil } from '@shared/util/notNil';
import { useResponsiveFormContext } from '../../ResponsiveFormContext';
import { AddMoreButton } from '../AddMoreButton';
import { EditFieldContainer } from '../EditFieldContainer';
import { RelatedEntityList } from './RelatedEntityList';
import { RelatedEntityPicker } from './RelatedEntityPicker';
import { RelatedQuantityList } from './RelatedQuantityList';

interface Props {
  field: RelatedEntityField | null;
  component: API.RelatedCardComponent;
  updateField: (componentId: string, field: RelatedEntityField | null) => void;
  fields: FieldRecord;
  error?: string;
  allowCreate?: boolean;
  allowClear?: boolean;
  style?: CSSProperties;
}

export function RelatedEntityEditField({
  component,
  field,
  updateField,
  fields,
  error,
  allowCreate = true,
  allowClear = true,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const hasQuantity = component.hasQuantity;
  const anchorName = getAnchorName('getAnchorName', 'related-entity', component.id);
  const [isPickerOpen, openPicker, closePicker] = useBoolean(false);
  const items = useMemo(() => field?.items ?? [], [field]);
  const { entities, loading: workflowEntitiesLoading } = useRelatedEntities(
    field,
    component.workflowTemplateId ?? null,
  );
  const [openingEntity, setOpeningEntity] = useState<RelatedEntity | null>(null);
  const isMisconfigured = !component.workflowTemplateId;

  const onChange = useCallback(
    (updatedEntities: API.WorkflowEntity[]): void => {
      const updatedItems = updatedEntities.map(entity => {
        const match = items.find(item => item.id === entity.id);
        return match ?? { id: entity.id, quantity: hasQuantity ? 1 : undefined };
      });
      updateField(component.id, new RelatedEntityField(updatedItems, updatedEntities));
    },
    [component.id, updateField, items, hasQuantity],
  );

  const updateItemQuantity = useCallback(
    (id: string, quantity: number) => {
      const updatedItems = items.map(item => {
        return item.id === id ? { ...item, quantity } : item;
      });
      updateField(component.id, new RelatedEntityField(updatedItems, entities));
    },
    [component.id, updateField, items, entities],
  );

  const hasEntities = !isEmpty(items) || workflowEntitiesLoading;
  const showEntities = !workflowEntitiesLoading && hasEntities;
  const label = hasQuantity
    ? getLabelWithCountAndType(component, items)
    : getLabelWithCount(component, entities.length);
  const isReadOnly = !!component.readonly || isMisconfigured;
  const printContent = hasQuantity
    ? getPrintEntitesWithQuantity(entities, items)
    : getPrintEntitiesWithoutQuantity(entities);
  const openEntity = useOpenRelatedEntity();
  const showClearButton = hasEntities && !isReadOnly && allowClear;

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const onItemPress = useCallback(
    (entity?: API.WorkflowEntity) => {
      if (isNil(entity)) {
        return;
      }
      setOpeningEntity(entity);
      openEntity(entity);
      setTimeout(() => {
        setOpeningEntity(null);
      }, 500);
    },
    [openEntity],
  );

  const onItemRemove = useCallback(
    (item?: RelatedItem) => {
      if (isNil(item)) {
        return;
      }
      updateField(
        component.id,
        new RelatedEntityField(
          items.filter(e => e.id !== item.id),
          entities.filter(e => e.id !== item.id),
        ),
      );
    },
    [component.id, items, entities, updateField],
  );

  return (
    <>
      <TouchableOpacity
        onPress={isReadOnly ? undefined : openPicker}
        disabled={hasEntities || workflowEntitiesLoading || isReadOnly}
      >
        <EditFieldContainer
          component={component}
          anchorName={anchorName}
          label={label}
          showLabel={responsiveForms || hasEntities}
          showPlaceholder={!hasEntities}
          showClearButton={showClearButton}
          onClear={onClear}
          error={error}
          subfooter={
            <WarningSubfooter
              showWarning={isMisconfigured}
              message={i18n('workflowTemplate.component.error.misconfigured')}
            />
          }
          isGrayScale={isMisconfigured}
          testID={!hasEntities ? getTestId('add-more-button', component.id) : undefined}
          footer={
            !isReadOnly &&
            hasEntities &&
            responsiveForms && (
              <View row>
                <AddMoreButton component={component} onPress={openPicker} />
              </View>
            )
          }
          style={style}
          printContent={printContent}
        >
          {workflowEntitiesLoading && <MBLoadingIndicator style={{ alignItems: 'flex-start' }} />}
          {showEntities && hasQuantity && (
            <RelatedQuantityList
              component={component}
              items={items}
              entities={entities}
              openPicker={openPicker}
              updateItemQuantity={updateItemQuantity}
              onItemPress={isReadOnly ? undefined : onItemPress}
              onItemRemove={isReadOnly ? undefined : onItemRemove}
              openingEntity={openingEntity}
              loading={workflowEntitiesLoading}
            />
          )}
          {showEntities && !hasQuantity && (
            <RelatedEntityList
              items={items}
              entities={entities}
              onItemPress={isReadOnly ? undefined : onItemPress}
              onItemRemove={isReadOnly ? undefined : onItemRemove}
              openPicker={openPicker}
              isReadOnly={isReadOnly}
              component={component}
              openingEntity={openingEntity}
              loading={workflowEntitiesLoading}
            />
          )}
        </EditFieldContainer>
      </TouchableOpacity>
      {isPickerOpen && (
        <RelatedEntityPicker
          entities={entities}
          onChange={onChange}
          onClose={closePicker}
          anchorName={anchorName}
          component={component}
          field={field}
          fields={fields}
          canCreateNew={allowCreate}
        />
      )}
    </>
  );
}

export function useOpenRelatedEntity() {
  const navigator = useNavigator();
  const getChannel = useGetChannel();
  const getWorkflowTemplate = useGetWorkflowTemplateLazyQuery();

  return useCallback(
    async (entity: RelatedEntity) => {
      const { channel } = await getChannel(entity.channelId);
      const workflowTemplate = await getWorkflowTemplate(entity.workflowTemplateId);

      if (!channel || !workflowTemplate) {
        return;
      }

      openCard({
        cardId: entity.id,
        channelSlug: channel.slug,
        selectorFields: {},
        navigator,
        behavior: workflowTemplate?.behavior ?? undefined,
      });
    },
    [getChannel, getWorkflowTemplate, navigator],
  );
}

function getLabelWithCountAndType(
  component: API.RelatedCardComponent,
  items: RelatedItem[],
): string {
  const count = items.length;
  const totalQuantity = sum(items.map(item => item.quantity ?? 0));
  const componentLabel = component.label ?? '';
  return i18n('card.blocks.cardsInput.quantity.label', {
    label: componentLabel,
    totalCount: totalQuantity,
    count,
  });
}

function getLabelWithCount(component: API.RelatedCardComponent, count: number) {
  if (count > 0) {
    const componentLabel = getComponentLabel(component);
    return `${componentLabel} (${count})`.trim();
  } else {
    return undefined;
  }
}

function getPrintEntitesWithQuantity(entities: RelatedEntity[], items: RelatedItem[]) {
  return entities
    .map(entity => {
      const item = items.find(i => i.id === entity.id);
      return `${entity.fields?.name} (${item?.quantity ?? 0})`;
    })
    .filter(notNil)
    .join('\n');
}

function getPrintEntitiesWithoutQuantity(entities: RelatedEntity[]) {
  return entities
    .map(entity => entity.fields?.name)
    .filter(notNil)
    .join(', ');
}
